import React, {
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  ArrowLeft,
  Search,
} from "lucide-react";
import {
  useNavigate,
  useParams,
} from "react-router-dom";

import { useAuth } from "../../../../contexts/AuthContext";
import { getAccessToken } from "../../../../awsAuth";
import ProfilePosts from "../../ProfilePosts";

import "./BusinessPostsAll.scss";

const API_BASE =
  process.env.REACT_APP_API_URL ||
  "https://cvanto-api.onrender.com";

function timeAgo(ts) {
  if (!ts) return "";

  const date = new Date(ts);
  const now = new Date();

  const diffMs =
    now.getTime() - date.getTime();

  const diffMinutes =
    Math.floor(diffMs / 60000);

  const diffHours =
    Math.floor(diffMinutes / 60);

  const diffDays =
    Math.floor(diffHours / 24);

  if (diffMinutes < 1) {
    return "щойно";
  }

  if (diffMinutes < 60) {
    return `${diffMinutes} хв тому`;
  }

  if (diffHours < 24) {
    return `${diffHours} год тому`;
  }

  if (diffDays === 1) {
    return "вчора";
  }

  const months = [
    "січня",
    "лютого",
    "березня",
    "квітня",
    "травня",
    "червня",
    "липня",
    "серпня",
    "вересня",
    "жовтня",
    "листопада",
    "грудня",
  ];

  return `${date.getDate()} ${
    months[date.getMonth()]
  }`;
}

export default function BusinessPostsAll() {
  const navigate = useNavigate();
  const params = useParams();
  const { userId } = useAuth();

  const viewedUserId =
    params.userId || userId;

  const [posts, setPosts] =
    useState([]);

  const [businessName, setBusinessName] =
    useState("Пости");

  const [avatar, setAvatar] =
    useState("");

  const [query, setQuery] =
    useState("");

  const [loading, setLoading] =
    useState(true);

  const [error, setError] =
    useState("");

  useEffect(() => {
    let alive = true;

    async function loadPosts() {
      if (!viewedUserId) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError("");

        const token =
          await getAccessToken()
            .catch(() => null);

        const headers = token
          ? {
              Authorization:
                `Bearer ${token}`,
            }
          : {};

        const [
          postsResponse,
          profileResponse,
        ] = await Promise.all([
          fetch(
            `${API_BASE}/api/posts/user/${encodeURIComponent(
              viewedUserId
            )}`,
            {
              headers,
            }
          ),

          fetch(
            `${API_BASE}/api/business/profile/${encodeURIComponent(
              viewedUserId
            )}`,
            {
              headers,
            }
          ),
        ]);

        const postsData =
          await postsResponse
            .json()
            .catch(() => []);

        const profileData =
          await profileResponse
            .json()
            .catch(() => ({}));

        if (!postsResponse.ok) {
          throw new Error(
            postsData?.error ||
              "Не вдалося завантажити пости"
          );
        }

        if (!alive) return;

        const items = Array.isArray(
          postsData
        )
          ? postsData
          : Array.isArray(
              postsData?.items
            )
          ? postsData.items
          : [];

        const userBase =
          profileData?.userBase || {};

        const profile =
          profileData?.profile || {};

        setPosts(items);

        setBusinessName(
          userBase.username ||
            profile.title ||
            profile.hero?.title ||
            "Пости"
        );

        setAvatar(
          userBase.avatar || ""
        );
      } catch (err) {
        console.error(err);

        if (alive) {
          setError(
            err.message ||
              "Помилка завантаження"
          );
        }
      } finally {
        if (alive) {
          setLoading(false);
        }
      }
    }

    loadPosts();

    return () => {
      alive = false;
    };
  }, [viewedUserId]);

  const filteredPosts =
    useMemo(() => {
      const safePosts =
        Array.isArray(posts)
          ? [...posts]
          : [];

      const normalizedQuery =
        query.trim().toLowerCase();

      if (!normalizedQuery) {
        return safePosts;
      }

      return safePosts.filter(
        (post) => {
          const text = [
            post?.title,
            post?.text,
            post?.description,
            post?.caption,
          ]
            .filter(Boolean)
            .join(" ")
            .toLowerCase();

          return text.includes(
            normalizedQuery
          );
        }
      );
    }, [posts, query]);

  if (loading) {
    return (
      <main className="businessPostsAll">
        <div className="businessPostsAll__state">
          Завантаження постів...
        </div>
      </main>
    );
  }

  if (error) {
    return (
      <main className="businessPostsAll">
        <div className="businessPostsAll__state businessPostsAll__state--error">
          {error}
        </div>
      </main>
    );
  }

  return (
    <main className="businessPostsAll">
      <header className="businessPostsAll__header">
        <button
          type="button"
          className="businessPostsAll__back"
          onClick={() => navigate(-1)}
          aria-label="Назад"
        >
          <ArrowLeft
            size={22}
            strokeWidth={2.4}
          />
        </button>

        <div className="businessPostsAll__heading">
          <span>Бізнес-профіль</span>

          <h1>Пости</h1>

          <p>
            {businessName} ·{" "}
            {posts.length} публікацій
          </p>
        </div>
      </header>

      <section className="businessPostsAll__search">
        <Search
          size={18}
          strokeWidth={2.4}
        />

        <input
          value={query}
          onChange={(event) =>
            setQuery(
              event.target.value
            )
          }
          placeholder="Пошук у постах"
        />
      </section>

      {filteredPosts.length === 0 ? (
        <section className="businessPostsAll__empty">
          <strong>
            Постів не знайдено
          </strong>

          <p>
            Спробуй змінити пошуковий запит.
          </p>
        </section>
      ) : (
        <section className="businessPostsAll__content">
          <ProfilePosts
            posts={filteredPosts}
            userMeta={{
              userName:
                businessName,
              avatar,
              effectiveUserId:
                viewedUserId,
            }}
            onPatchPosts={setPosts}
            currentUserId={userId}
            apiBase={API_BASE}
            timeAgo={timeAgo}
            userId={viewedUserId}
          />
        </section>
      )}
    </main>
  );
}